// frontend/src/components/players/PDFViewer.js
import React, { useState, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  ChevronRight,
  ChevronLeft,
  FileText,
  Loader,
  RefreshCw,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { convertToPersianNumbers } from '../../utils/PersianNumberUtils';
import { useSecureMedia } from '../../services/mediaService';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDFViewer = ({ mediaId, fileUrl, title }) => {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);

  const [scale, setScale] = useState(1.0);
  const [containerWidth, setContainerWidth] = useState(800);

  const { mediaUrl, isLoading: urlLoading, error: urlError, refetch: refetchMediaUrl } =
    useSecureMedia(mediaId);

  const pdfFile = fileUrl || mediaUrl;

  useEffect(() => {
    const container = document.querySelector('.pdf-document-container');
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPageNumber(1);
  }, []);

  const onPageRenderSuccess = useCallback(() => {
    setPageLoading(false);
  }, []);

  const goToPrevPage = () => {
    if (pageNumber <= 1) return;
    setPageLoading(true);
    setPageNumber((prev) => prev - 1);
  };

  const goToNextPage = () => {
    if (pageNumber >= numPages) return;
    setPageLoading(true);
    setPageNumber((prev) => prev + 1);
  };

  const handleRetry = () => {
    setPdfLoading(true);
    if (urlError) refetchMediaUrl();
  };

  if (urlError) {
    return (
      <div className="p-8 text-center text-red-400 bg-white h-screen flex flex-col items-center justify-center rtl">
        <FileText size={48} className="mb-4" />
        <p className="font-bold mb-4">{urlError}</p>
        <button
          onClick={handleRetry}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
        >
          <RefreshCw size={18} /> تلاش مجدد
        </button>
      </div>
    );
  }

  if (urlLoading) {
    return (
      <div className="p-8 text-center text-gray-600 bg-white h-screen flex flex-col items-center justify-center rtl">
        <Loader size={48} className="animate-spin mb-4" />
        <p>در حال دریافت لینک فایل...</p>
      </div>
    );
  }

  return (
    <div className="pdf-viewer flex flex-col h-screen bg-white">
      {/* نوار ابزار بالایی – همهٔ کنترل‌ها اینجاست */}
      <div className="w-full bg-gray-800 text-white py-2 px-4 flex items-center justify-between shadow-md flex-shrink-0">
        <span className="text-sm font-medium truncate">{title || 'سند PDF'}</span>

        <div className="flex items-center gap-4">
          {numPages > 0 && (
            <>
              <button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1 || pageLoading}
                className={`p-1 rounded hover:bg-gray-700 transition ${
                  pageNumber <= 1 || pageLoading ? 'text-gray-500 cursor-not-allowed' : ''
                }`}
                title="صفحه قبلی"
              >
                <ChevronRight size={18} />
              </button>

              <span className="text-xs font-medium min-w-[80px] text-center">
                {convertToPersianNumbers(pageNumber)} / {convertToPersianNumbers(numPages)}
              </span>

              <button
                onClick={goToNextPage}
                disabled={pageNumber >= numPages || pageLoading}
                className={`p-1 rounded hover:bg-gray-700 transition ${
                  pageNumber >= numPages || pageLoading ? 'text-gray-500 cursor-not-allowed' : ''
                }`}
                title="صفحه بعدی"
              >
                <ChevronLeft size={18} />
              </button>
            </>
          )}

          <div className="flex items-center gap-2 border-r border-gray-600 pr-4 mr-2">
            <button onClick={() => setScale((s) => Math.max(0.5, s - 0.1))} className="p-1 hover:bg-gray-700 rounded" title="کوچک‌نمایی">
              <ZoomOut size={18} />
            </button>
            <span className="text-xs w-10 text-center">{Math.round(scale * 100)}%</span>
            <button onClick={() => setScale((s) => Math.min(3, s + 0.1))} className="p-1 hover:bg-gray-700 rounded" title="بزرگ‌نمایی">
              <ZoomIn size={18} />
            </button>
            <button onClick={() => setScale(1.0)} className="px-2 py-0.5 text-xs bg-gray-600 hover:bg-gray-500 rounded">۱:۱</button>
          </div>
        </div>
      </div>

      {/* محتوای PDF – پس‌زمینه سفید، اسکرول عمودی */}
      <div className="pdf-document-container flex-1 overflow-y-auto p-4 flex justify-center bg-white">
        <Document
          file={pdfFile}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={console.error}
          loading={
            <div className="flex items-center justify-center h-64 text-gray-600">
              <Loader size={32} className="animate-spin ml-2" />
              <span>در حال بارگذاری فایل PDF...</span>
            </div>
          }
          error={
            <div className="text-center py-10 text-red-500">
              <p className="font-bold">خطا در بارگذاری سند PDF</p>
              <button onClick={handleRetry} className="mt-3 text-sm underline">تلاش مجدد</button>
            </div>
          }
        >
          {!pdfLoading && (
            <div className="relative">
              {pageLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
                  <Loader className="animate-spin" size={32} />
                </div>
              )}
              <Page
                pageNumber={pageNumber}
                width={containerWidth * scale}
                onRenderSuccess={onPageRenderSuccess}
                onRenderError={() => setPageLoading(false)}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={
                  <div className="flex justify-center py-20">
                    <Loader className="animate-spin" />
                  </div>
                }
              />
            </div>
          )}
        </Document>
      </div>
    </div>
  );
};

export default PDFViewer;